import React, { useEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import { Icon } from '@iq/react-components';

import { useBackgroundFreeze, useForceUpdate, useParentOnlyClickHandler } from '../../utils';

const modalRoot = document.querySelector('#global-modal-container');

// prevents accordion list items' edit modals from toggling accordion
const clickHandler = (event) => event.stopPropagation();

const SimpleModal = ({
  children,
  onClose: defaultOnClose,
  scrollResetKey,
  headerLeft,
  headerCenter,
  headerRight,
  noHeader = '',
  noBodyPadding = false,
  title,
  subtitle,
  className = '',
  containerClass = '',
  size = 'm',
  overlayCanClose = true,
}) => {
  const [didRender, setDidRender] = useState(false);
  const containerRef = useRef();
  const overlayReference = useRef();
  const forceUpdate = useForceUpdate();

  const onClose =
    typeof defaultOnClose === 'function'
      ? (event) => {
          if (event.target === event.currentTarget) {
            defaultOnClose(event);
          }
        }
      : defaultOnClose;

  const keyListener = (event) => {
    if (event.keyCode === 27) {
      const topModal = document.querySelector('#global-modal-container > div:last-child');
      if (topModal === containerRef.current) {
        defaultOnClose(event);
      }
    }
  };

  useParentOnlyClickHandler(onClose, overlayReference.current, overlayCanClose);
  useBackgroundFreeze();

  useEffect(() => {
    if (containerRef.current?.scrollTo) {
      document.addEventListener('keydown', keyListener);
      setDidRender(true);
      containerRef.current.scrollTo(0, 0);
    }
    return () => {
      document.removeEventListener('keydown', keyListener);
    };
  }, [containerRef.current]);

  useEffect(() => {
    if (containerRef.current) {
      containerRef.current.scrollTo(0, 0);
    }
  }, [scrollResetKey]);

  if (!didRender) setTimeout(forceUpdate);

  return ReactDOM.createPortal(
    <div
      className={`simple-modal-container ${containerClass}`}
      onClick={clickHandler}
      ref={containerRef}
    >
      <div
        ref={overlayReference}
        data-testid="simple-modal-overlay"
        className="simple-modal-overlay"
        style={{ cursor: overlayCanClose ? 'pointer' : 'default' }}
      >
        <div
          className={`simple-modal-wrapper ${className} size-${size} ${noHeader} ${noBodyPadding ? 'padding-none' : ''}`}
        >
          {(title !== undefined || defaultOnClose !== undefined || subtitle !== undefined) &&
            noHeader !== 'noHeader' && (
              <div className="simple-modal-header">
                {(title !== undefined ||
                  defaultOnClose !== undefined ||
                  headerLeft !== undefined ||
                  headerCenter !== undefined ||
                  headerRight !== undefined) && (
                  <div className="simple-modal-main-header">
                    <div className="simple-modal-main-header__left">
                      {headerLeft !== undefined && <div className="header-left">{headerLeft}</div>}
                      {title !== undefined && <div className="title">{title}</div>}
                    </div>
                    <div className="simple-modal-main-header__center">
                      {headerCenter !== undefined && (
                        <div className="header-center">{headerCenter}</div>
                      )}
                    </div>
                    <div className="simple-modal-main-header__right">
                      {headerRight !== undefined && (
                        <div className="header-right">{headerRight}</div>
                      )}
                      {defaultOnClose !== undefined && (
                        <div>
                          <div
                            data-testid="modal-close-button"
                            className="close"
                          >
                            <Icon
                              size="xs"
                              icon="he-close"
                              onClick={defaultOnClose}
                            />
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                )}
                {subtitle !== undefined && (
                  <div className="simple-modal-sub-header">
                    {subtitle !== undefined && <div className="subtitle">{subtitle}</div>}
                  </div>
                )}
              </div>
            )}
          <div className={`simple-modal-body ${noBodyPadding ? 'padding-none' : ''}`}>
            {children}
          </div>
        </div>
      </div>
    </div>,
    modalRoot
  );
};

export default SimpleModal;
